import { InputGroup, InputGroupButton, InputGroupInput } from '@sho-bot/ui';
import { IconArrowRight } from '@tabler/icons-react';
import { useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { API_ERROR_LABELS, type ApiErrorCode } from '../../shared/errors.js';
import { ApiRequestError } from '../api/client.js';
import { useSession } from '../session.js';

/**
 * The operator login.
 *
 * A real `<form>` with a real `<input type="password">` and an
 * `autocomplete="current-password"`, so a password manager recognizes it and
 * fills it. That is not a nicety: the alternative to a manager filling a
 * 32-character random secret is an operator choosing something they can type.
 *
 * The error region exists in the DOM from the first render with a stable id, is
 * `aria-live="polite"`, and is referenced by the input's `aria-describedby`. A
 * region that appears only on failure is announced inconsistently across screen
 * readers; one that is always there is announced every time its content changes.
 *
 * Focus returns to the field after a rejection, because the next thing the
 * operator will do is type in it.
 */
export function LoginPage(): ReactNode {
  const { signIn } = useSession();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const errorId = useId();

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return;

    if (password.length === 0) {
      setError('Enter the admin password.');
      inputRef.current?.focus();
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await signIn(password);
      // Cleared on success as well as failure: there is no reason for the
      // secret to stay in a React state cell after it has been used.
      setPassword('');
    } catch (caught) {
      const code: ApiErrorCode = caught instanceof ApiRequestError ? caught.code : 'internal_error';
      const retryAfter = caught instanceof ApiRequestError ? caught.retryAfterSeconds : undefined;
      setError(
        retryAfter === undefined
          ? API_ERROR_LABELS[code]
          : `${API_ERROR_LABELS[code]} Try again in ${String(Math.ceil(retryAfter / 60))} minutes.`,
      );
      setPassword('');
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-bg-shell p-4">
      <section className="w-full max-w-[17rem]">
        <form onSubmit={(event) => void onSubmit(event)} noValidate>
          <InputGroup>
            <InputGroupInput
              ref={inputRef}
              type="password"
              name="password"
              autoComplete="current-password"
              autoFocus
              required
              value={password}
              aria-label="Admin password"
              aria-describedby={errorId}
              aria-invalid={error !== null}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
            <InputGroupButton
              type="submit"
              size="icon-xs"
              aria-label="Sign in"
              loading={submitting}
            >
              <IconArrowRight aria-hidden="true" stroke={1.5} />
            </InputGroupButton>
          </InputGroup>

          {/* Present from first render, so a change to it is reliably announced. */}
          <p
            id={errorId}
            role="alert"
            aria-live="polite"
            className="mt-2 min-h-5 text-sm text-destructive"
          >
            {error ?? ''}
          </p>
        </form>
      </section>
    </main>
  );
}
